<template>
    <div v-if="show" class="modal-overlay" @click.self="close">
        <div class="modal-content">
            <div class="modal-header">
                <h3>{{ visitor.first_name }} {{ visitor.last_name }}</h3>
                <button class="close-button" @click="close">&times;</button>
            </div>
            
            <div class="contact-info">
                <p v-if="visitor.company" class="company-info">{{ visitor.company }}</p>
                <div class="contact-links">
                    <div class="contact-item">
                        <span class="contact-label">Email:</span>
                        <a :href="`mailto:${visitor.email}`" class="contact-link email-link">
                            {{ visitor.email }}
                        </a>
                    </div>
                    <div v-if="visitor.phone" class="contact-item">
                        <span class="contact-label">Phone:</span>
                        <a :href="`tel:${visitor.phone}`" 
                           class="contact-link phone-link">
                            {{ visitor.phone }}
                        </a>
                    </div>
                </div>
            </div>

            <div class="visit-history">
                <h4>Visit History</h4>
                <div v-if="loading" class="loading">Loading visit history...</div>
                <div v-else-if="visits.length === 0" class="no-visits">No previous visits found</div>
                <table v-else class="visit-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Event</th>
                            <th>Host</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="visit in visits" :key="visit.id">
                            <td>{{ formatDate(visit.event_date) }}</td>
                            <td>{{ formatType(visit.attendee_type) }}</td>
                            <td>{{ visit.event_name }}</td>
                            <td>{{ visit.host_name }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Admin Actions -->
            <div v-if="isAdmin" class="admin-actions">
                <button
                    class="convert-button"
                    @click="convertToMember"
                    :disabled="converting || existingMember"
                >
                    {{ converting ? 'Converting...' : 'Convert to Member' }}
                </button>
                <p v-if="existingMember" class="info-message">
                    Already a member. Activate in Member Admin.
                </p>
                <p v-if="conversionMessage" class="conversion-message" :class="conversionMessageType">
                    {{ conversionMessage }}
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { format } from 'date-fns';
import { eventVisitorService } from '../services/eventVisitorService';
import { visitorService } from '../services/visitorService';

export default {
    name: 'VisitorDetailsModal',
    props: {
        show: Boolean,
        visitor: Object
    },
    emits: ['close', 'visitor-converted'],
    setup(props, { emit }) {
        const visits = ref([]);
        const loading = ref(false);
        const converting = ref(false);
        const conversionMessage = ref('');
        const conversionMessageType = ref('');
        const existingMember = ref(false);

        const currentUser = computed(() => {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        });

        const isAdmin = computed(() => {
            return currentUser.value && currentUser.value.role === 'admin';
        });

        const loadVisitHistory = async () => {
            if (!props.visitor?.id) return;

            loading.value = true;
            try {
                const history = await eventVisitorService.getVisitorHistory(props.visitor.id);
                visits.value = history;
            } catch (error) {
                console.error('Error loading visit history:', error);
            } finally {
                loading.value = false;
            }
        };

        const checkExistingMember = async () => {
            if (!props.visitor?.id) return;

            try {
                const result = await visitorService.checkExistingMember(props.visitor.id);
                existingMember.value = result.exists;
            } catch (error) {
                console.error('Error checking existing member:', error);
                existingMember.value = false;
            }
        };

        const convertToMember = async () => {
            if (!props.visitor?.email) {
                conversionMessage.value = 'Visitor must have an email address to be converted to a member.';
                conversionMessageType.value = 'error';
                return;
            }

            const confirmed = confirm(
                `Are you sure you want to convert ${props.visitor.first_name} ${props.visitor.last_name} to a member?\n\n` +
                `An account will be created with email: ${props.visitor.email}\n` +
                `They will receive a welcome email with instructions to set their password.`
            );

            if (!confirmed) return;

            converting.value = true;
            conversionMessage.value = '';
            conversionMessageType.value = '';

            try {
                await visitorService.convertToMember(props.visitor.id);

                conversionMessage.value = `Successfully converted ${props.visitor.first_name} ${props.visitor.last_name} to a member! They will receive a welcome email.`;
                conversionMessageType.value = 'success';

                // Emit event to parent component
                emit('visitor-converted', props.visitor.id);

                // Close modal after a delay
                setTimeout(() => {
                    close();
                }, 2000);
            } catch (error) {
                console.error('Error converting visitor to member:', error);
                conversionMessage.value = error.response?.data?.error || 'Failed to convert visitor to member. Please try again.';
                conversionMessageType.value = 'error';
            } finally {
                converting.value = false;
            }
        };

        const formatDate = (date) => {
            return format(new Date(date), 'MMM d, yyyy');
        };

        const formatType = (type) => {
            return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
        };

        const close = () => {
            conversionMessage.value = '';
            conversionMessageType.value = '';
            emit('close');
        };

        onMounted(() => {
            loadVisitHistory();
            checkExistingMember();
        });

        // Add watch to reload when visitor changes
        watch(() => props.visitor, () => {
            if (props.show) {
                loadVisitHistory();
                checkExistingMember();
            }
        }, { deep: true });

        return {
            visits,
            loading,
            converting,
            conversionMessage,
            conversionMessageType,
            existingMember,
            isAdmin,
            formatDate,
            formatType,
            convertToMember,
            close
        };
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 8px;
    padding: 2rem;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
}

.modal-header h3 {
    font-size: 1.4rem;
    margin: 0;
}

.close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
}

.contact-info {
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #eee;
    text-align: left;
}

.company-info {
    font-size: 1.1rem;
    margin: 0.25rem 0 1rem 0;
    color: #666;
}

.contact-links {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.contact-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.contact-label {
    color: #666;
    font-size: 0.9rem;
    font-weight: bold;
}

.contact-link {
    color: #4285f4;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
}

.contact-link:hover {
    text-decoration: underline;
}

.visit-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.visit-table th {
    text-align: left;
    padding: 0.75rem;
    border-bottom: 2px solid #eee;
    font-weight: 600;
    color: #2c3e50;
}

.visit-table td {
    text-align: left;
    padding: 0.75rem;
    border-bottom: 1px solid #eee;
    color: #666;
}

.visit-table tr:hover {
    background-color: #f8f9fa;
}

.loading, .no-visits {
    text-align: center;
    color: #666;
    padding: 1rem;
}

.admin-actions {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
    text-align: center;
}

.convert-button {
    background-color: #28a745;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
}

.convert-button:hover:not(:disabled) {
    background-color: #218838;
}

.convert-button:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
}

.conversion-message {
    margin-top: 1rem;
    padding: 0.75rem;
    border-radius: 4px;
    font-size: 0.95rem;
}

.conversion-message.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.conversion-message.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.info-message {
    margin-top: 1rem;
    padding: 0.75rem;
    border-radius: 4px;
    font-size: 0.95rem;
    background-color: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
}

/* Mobile styles */
@media (max-width: 768px) {
    .modal-content {
        width: 100%;
        height: 100%;
        max-height: 100%;
        border-radius: 0;
        padding: 1rem;
    }

    .modal-header h3 {
        font-size: 1.2rem;
    }

    .contact-links {
        flex-direction: column;
        gap: 0.5rem;
    }

    .contact-item {
        width: 100%;
    }

    .visit-table {
        font-size: 0.9rem;
    }

    .visit-table th,
    .visit-table td {
        padding: 0.5rem;
    }
}
</style> 